<template>
	<div class="flex flex-col gap-1 text-gray-950">
		<p class="flex">
			<span
				:class="[
					size === 'medium'
						? 'text-2xl font-medium'
						: size === 'large'
							? 'text-lg font-bold'
							: 'text-[15px] font-bold'
				]"
			>
				{{ formattedPrice }}
			</span>
			<span
				v-if="isDiscountedProduct()"
				:class="[
					'pl-2 line-through text-gray-super-dark',
					size === 'medium'
						? 'text-2xl font-medium'
						: size === 'large'
							? 'text-lg font-normal'
							: 'text-[15px] font-normal'
				]"
			>
				{{ formattedOldPrice }}
			</span>
		</p>
		<div
			v-if="isProductPage && historicalLowestPrice"
			class="text-xs text-left"
		>
			<Popover class="relative">
				<PopoverButton
					class="flex items-center gap-1 outline-none text-gray-super-dark"
				>
					<InformationCircleIcon class="w-5 h-5 min-w-5" />
					{{ $t("View price history") }}
				</PopoverButton>
				<PopoverPanel
					class="absolute top-6 left-0 z-10 py-1 px-2 min-w-56 max-h-[480px] bg-white border border-gray-600"
				>
					{{
						$t("Lowest price in the last 30 days: {value}", {
							value: formatPriceWithSymbol(historicalLowestPrice)
						})
					}}
				</PopoverPanel>
			</Popover>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { currencyMap } from "@/utils/currencyConfig";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";

interface Props {
	price: number;
	oldPrice?: number | null;
	size?: string;
	isProductPage?: boolean;
}

const { size, oldPrice, price } = defineProps<Props>();

const norceStore = useNorceStore();
const { lowestPrice: historicalLowestPrice } = storeToRefs(useProductStore());

const currencySettings = computed(() => {
	const currencyCode = norceStore?.currentCurrency?.Code;
	return (
		currencyMap[currencyCode] || { symbol: currencyCode, position: "prefix" }
	);
});

const formatPriceWithSymbol = (price: number) => {
	const { symbol, position } = currencySettings.value;
	const formattedPrice = Number.isInteger(price) ? price : price.toFixed(2);
	return position === "prefix"
		? `${symbol} ${formattedPrice}`
		: `${formattedPrice} ${symbol}`;
};

const formattedPrice = computed(() => formatPriceWithSymbol(price));
const formattedOldPrice = computed(() =>
	oldPrice ? formatPriceWithSymbol(oldPrice) : ""
);

const isDiscountedProduct = () => oldPrice && oldPrice > price;
</script>
